import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadAllMarkdown } from '../utils/markdownLoader.js';
import { getArticleImage } from '../data/articleImages.js';
import StaticSeo from '../components/StaticSeo.jsx';

// Lazy glob — articles are loaded in parallel on mount.
// Session 41 — points at enriched dir for consistency with ArticlePage
// (the listing only reads frontmatter, which passes through unchanged,
// but using the same glob target keeps the data path uniform).
const articleModules = import.meta.glob('../articles/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!match) return { frontmatter: {} };
  const yamlText = match[1];
  const fm = {};
  for (const line of yamlText.split('\n')) {
    const m = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*"?([^"]*)"?$/);
    if (m && !line.startsWith(' ')) {
      fm[m[1]] = m[2].trim();
    }
  }
  return { frontmatter: fm };
}

const PILLAR_INFO = {
  encyclopedia: { label: 'The Encyclopedia', italic: 'Encyclopedia', desc: 'Every surface, every tool, every problem — documented.' },
  diagnostician: { label: 'The Diagnostician', italic: 'Diagnostician', desc: "Start with what's wrong. Find the cause." },
  'field-notes': { label: 'Field Notes', italic: 'Notes', desc: 'Reported essays. Original photography.' },
  'pro-track': { label: 'The Pro Track', italic: 'Pro Track', desc: 'Insider knowledge for the working cleaner.' },
};

export default function ArticleIndex({ pillar }) {
  const [dark, setDark] = useState(false);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadAllMarkdown(articleModules, parseFrontmatter).then((entries) => {
      if (cancelled) return;
      const list = entries
        .map((e) => e.frontmatter || {})
        .filter((fm) => !pillar || fm.pillar === pillar)
        .sort((a, b) => (a.articleNumber || '999').localeCompare(b.articleNumber || '999'));
      setArticles(list);
    });
    return () => { cancelled = true; };
  }, [pillar]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  const pillarMeta = pillar ? PILLAR_INFO[pillar] : null;

  const PILLAR_PATHS = {
    encyclopedia: '/encyclopedia',
    diagnostician: '/diagnostician',
    'field-notes': '/field-notes',
    'pro-track': '/pro-track',
  };
  // s80 (debt #7): title/description now come from src/data/seo.js via
  // <StaticSeo>; only the path is computed here.
  const seoPath = pillar ? (PILLAR_PATHS[pillar] || '/articles') : '/articles';

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, padding: '32px 24px 80px',
    }}>
      <StaticSeo schemaType="collectionPage" path={seoPath} />
      <style>{`
        .article-row { transition: background 0.2s, padding-left 0.2s; }
        .article-row:hover { background: ${T.surface}; padding-left: 24px; }
        .ghost-link:hover { color: ${T.sienna}; }
        .ai-row {
          display: grid;
          grid-template-columns: 64px 72px 1fr 32px;
          gap: 20px;
          align-items: center;
          padding: 20px 0;
        }
        @media (max-width: 600px) {
          .ai-row {
            grid-template-columns: 52px 1fr 24px;
            gap: 14px;
          }
          .ai-row .ai-num { display: none; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22,
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 16,
        }}>
          {pillar ? `§ ${pillar.toUpperCase().replace('-', ' ')}` : '§ ALL ARTICLES'}
        </div>

        {pillarMeta ? (
          <>
            <h1 style={{
              fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
              lineHeight: 1.05, letterSpacing: '-0.025em',
              margin: '0 0 12px',
            }}>
              {pillarMeta.label.replace(pillarMeta.italic, '')}<em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>{pillarMeta.italic}</em>
            </h1>
            <p style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft, margin: '0 0 56px',
              maxWidth: 600,
            }}>
              {pillarMeta.desc}
            </p>
          </>
        ) : (
          <h1 style={{
            fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
            lineHeight: 1.05, letterSpacing: '-0.025em',
            margin: '0 0 56px',
          }}>
            All <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>articles</em>.
          </h1>
        )}

        {articles.length === 0 ? (
          <p style={{
            fontFamily: fontDisplay, fontSize: 18, fontStyle: 'italic',
            color: T.inkSoft,
          }}>
            No articles in this pillar yet. Check back soon — we publish weekly.
          </p>
        ) : (
          <div style={{ borderTop: `1px solid ${T.rule}` }}>
            {articles.map((a) => {
              const img = getArticleImage(a.slug);
              return (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                className="article-row ai-row"
                style={{
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{
                  width: 52, height: 52, flexShrink: 0,
                  background: T.surface, overflow: 'hidden',
                  border: `1px solid ${T.rule}`,
                }}>
                  {img && (
                    <img
                      src={img.thumb}
                      alt={img.alt}
                      width={52}
                      height={52}
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', display: 'block',
                      }}
                    />
                  )}
                </div>
                <div className="ai-num" style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                }}>
                  № {a.articleNumber || '—'}
                </div>
                <div>
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 22,
                    lineHeight: 1.25, marginBottom: 6,
                  }}>
                    {a.title}
                  </div>
                  {a.subtitle && (
                    <div style={{
                      fontFamily: fontBody, fontSize: 14,
                      color: T.inkSoft, lineHeight: 1.5,
                      maxWidth: 600,
                    }}>
                      {a.subtitle.length > 120 ? a.subtitle.slice(0, 120) + '…' : a.subtitle}
                    </div>
                  )}
                </div>
                <div style={{ color: T.inkFaint, textAlign: 'right' }}>→</div>
              </Link>
              );
            })}
          </div>
        )}

        {/* Back link */}
        <div style={{ marginTop: 56 }}>
          <Link to="/" className="ghost-link" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO HOMEPAGE
          </Link>
        </div>
      </div>
    </div>
  );
}
